import { useCallback, useMemo } from "react";
import { useNavigateWithSlug } from "@/router/useNavigateWithSlug";

import { targetToLocation } from "./routeCodec";
import { Tab, TabEntityRef, TabTarget, targetsSameEntity } from "./tabTarget";
import { useApplyTabsOp, useTabActions } from "./tabs";
import * as ops from "./tabsReducer";
import { TabsState, findTabById } from "./tabsReducer";

/**
 * Everything that can change which tab is active. Opening a tab means navigating to it and letting
 * `useSyncTabsWithRoute` fold the route change back into the strip; closing one computes where the
 * selection lands and then navigates there.
 */

export type OpenTabOptions = {
  /** Replace the history entry instead of pushing. Use when the previous URL is no longer valid. */
  replace?: boolean;
};

export function useOpenTab() {
  const navigate = useNavigateWithSlug();
  return useCallback(
    (target: TabTarget, options?: OpenTabOptions) => {
      navigate({ ...targetToLocation(target), replace: options?.replace });
    },
    [navigate],
  );
}

export function useOpenModelInTab() {
  const openTab = useOpenTab();
  return useCallback(
    (
      params: { modelId: string; dashboardId?: string; tileId?: string },
      options?: OpenTabOptions,
    ) => openTab({ kind: "model", ...params }, options),
    [openTab],
  );
}

/** Opens an unsaved draft. Its label comes from the drafts store, so none is passed in. */
export function useOpenDraftInTab() {
  const openTab = useOpenTab();
  return useCallback(
    (draftId: string, options?: OpenTabOptions) =>
      openTab({ kind: "draft", draftId }, options),
    [openTab],
  );
}

export function useOpenDashboardInTab() {
  const openTab = useOpenTab();
  return useCallback(
    (dashboardId: string, options?: OpenTabOptions) =>
      openTab({ kind: "dashboard", dashboardId }, options),
    [openTab],
  );
}

/**
 * Switches to a tab that is already open.
 *
 * For anything with a URL of its own this is just navigation. Scratch tabs all live at `/editor`, so
 * the route cannot say which one is meant — the selection is set directly first, and the route sync
 * then leaves an already-selected scratch tab alone.
 */
export function useSelectTab() {
  const { activateTab } = useTabActions();
  const openTab = useOpenTab();
  return useCallback(
    (tab: Tab) => {
      if (tab.target.kind === "scratch") activateTab(tab.id);
      openTab(tab.target);
    },
    [activateTab, openTab],
  );
}

/**
 * Adds an empty tab and switches to it. Route syncing cannot do this — every empty tab lives at
 * `/editor`, so the URL cannot say which one is meant.
 */
export function useOpenNewTab() {
  const { createScratchTab } = useTabActions();
  const openTab = useOpenTab();
  return useCallback(() => {
    createScratchTab();
    openTab({ kind: "scratch" });
  }, [createScratchTab, openTab]);
}

/**
 * Closing runs the op against whatever state is committed at the time, then follows the selection
 * the op actually landed on. Deriving the next tab from a render-time snapshot instead would let two
 * closes fired before a re-render each work from the same stale list, and the second would resurrect
 * what the first removed.
 *
 * Returned actions are identity-stable, so they are safe in effect dependency lists.
 */
export function useTabCloseActions() {
  const apply = useApplyTabsOp();
  const openTab = useOpenTab();

  const commit = useCallback(
    (op: (prev: TabsState) => TabsState, options?: OpenTabOptions) => {
      let transition: { prev: TabsState; next: TabsState } | undefined;
      // Reading the transition out of the updater is safe here in a way it would not be for a React
      // setState updater: `apply` is a Jotai write, invoked exactly once, never double-invoked
      // under StrictMode.
      apply((prev) => {
        const next = op(prev);
        transition = { prev, next };
        return next;
      });
      if (!transition) return;
      const { prev, next } = transition;
      if (next.activeTabId === prev.activeTabId) return;
      const nextActive = findTabById(next.tabs, next.activeTabId);
      openTab(nextActive?.target ?? { kind: "scratch" }, options);
    },
    [apply, openTab],
  );

  return useMemo(
    () => ({
      closeTab: (tabId: string) => commit((prev) => ops.closeTab(prev, tabId)),
      closeTabs: (tabIds: string[]) =>
        commit((prev) => ops.closeTabs(prev, tabIds)),
      closeActiveTab: () =>
        commit((prev) =>
          prev.activeTabId ? ops.closeTab(prev, prev.activeTabId) : prev,
        ),
      closeOtherTabs: (tabId: string) =>
        commit((prev) => ops.closeOtherTabs(prev, tabId)),
      closeTabsToTheRight: (tabId: string) =>
        commit((prev) => ops.closeTabsToTheRight(prev, tabId)),
      closeAllTabs: () => commit(() => ops.closeAllTabs()),
      /**
       * Closes the tabs pointing at an entity the user just removed. Replaces the history entry —
       * the URL the user is on points at something that no longer exists.
       */
      closeTabsForEntity: (ref: TabEntityRef) =>
        commit((prev) => ops.closeTabsForEntity(prev, ref), { replace: true }),
      /** Drops tabs whose target no longer resolves, without disturbing a surviving selection. */
      keepTabs: (shouldKeep: (tab: Tab) => boolean) =>
        commit((prev) => ops.keepTabs(prev, shouldKeep), { replace: true }),
    }),
    [commit],
  );
}

/**
 * Moves the tabs showing `ref` over to `target`, then opens `target`. For an unsaved draft that has
 * just been saved or published as a model.
 *
 * Opening the model and deleting the draft as two separate steps loses the selection: the stale-tab
 * pruner runs before the route sync, closes the draft tab and selects its neighbour.
 */
export function useReplaceTabEntity() {
  const apply = useApplyTabsOp();
  const openTab = useOpenTab();
  return useCallback(
    (ref: TabEntityRef, target: TabTarget) => {
      let wasShowingRef = false;
      apply((prev) => {
        const active = ops.getActiveTab(prev);
        wasShowingRef = active != null && targetsSameEntity(active.target, ref);
        return ops.replaceEntity(prev, ref, target);
      });
      // The URL the user is on points at something that no longer exists.
      openTab(target, { replace: wasShowingRef });
    },
    [apply, openTab],
  );
}

export function useCloseActiveTab() {
  return useTabCloseActions().closeActiveTab;
}

export function useCloseTabsForEntity() {
  return useTabCloseActions().closeTabsForEntity;
}
